// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import * as Sentry from "@sentry/react";
import { recordWebTelemetry } from "../utils/telemetry";
import { auth, db } from "../firebase";
import { CustomClaims, UserRole, UserProfile } from "@visitproof/types";
import { resolveActiveRole, setStoredActiveRole } from "../utils/roleUtils";
import { UserService } from "../services/UserService";

interface AuthContextProps {
  user: User | null;
  claims: CustomClaims | null;
  role: UserRole | null;
  loading: boolean;
  error: string | null;
  isMonitor: boolean;
  isAdmin: boolean;
  isParent: boolean;
  signOut: () => Promise<void>;
  clearError: () => void;
  refreshClaims: () => Promise<void>;
  // Role switching
  availableRoles: UserRole[];
  activeRole: UserRole | null;
  setActiveRole: (role: UserRole) => Promise<void>;
  canSwitchRoles: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const DEFAULT_CLAIMS: CustomClaims = {
  role: null,
  accountId: null,
  accountIds: null,
  supervisorIds: null,
  parentIds: null,
};

const PRODUKTLY_CLIENT_TOKEN = "32706ffe3da3ae9ed6c8b7810c5c5c5191449abc4b962f3eb2a9406db5664364b08173bee7d7217bde0bbf99d08a85cea7f33fc2d5fa95d301c16498187f67c670b7783794decc1061cde25472bf284263d1a68b633dcc5fda66d92eb16325d6ec70aeca";

// Load Produktly SDK dynamically (only for admins)
const loadProduktly = (userId: string) => {
  // Check if already loaded
  if (document.getElementById("produktlyScript")) {
    // Already loaded, just identify the user
    const produktly = (window as any).Produktly;
    if (produktly?.identifyUser) {
      produktly.identifyUser(userId);
    }
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://public.produktly.com/js/main.js";
  script.id = "produktlyScript";
  script.dataset.clientToken = PRODUKTLY_CLIENT_TOKEN;

  script.onload = () => {
    const produktly = (window as any).Produktly;
    if (produktly?.identifyUser) {
      produktly.identifyUser(userId);
    }
  };

  document.head.appendChild(script);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<CustomClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [activeRole, setActiveRoleState] = useState<UserRole | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const loadClaims = useCallback(async (currentUser: User) => {
    try {
      // Force refresh to get latest claims from server
      const tokenResult = await currentUser.getIdTokenResult(true);
      setClaims(tokenResult.claims as unknown as CustomClaims);
    } catch (err) {
      console.error("Error loading claims:", err);
      setClaims(DEFAULT_CLAIMS);
    }
  }, []);

  const refreshClaims = useCallback(async () => {
    if (user) {
      try {
        const tokenResult = await user.getIdTokenResult(true); // Force refresh
        setClaims(tokenResult.claims as unknown as CustomClaims);
      } catch (err) {
        console.error("Error refreshing claims:", err);
      }
    }
  }, [user]);

  const handleSignOut = useCallback(async () => {
    try {
      setError(null);
      await signOut(auth);
      localStorage.clear();
      setClaims(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign out');
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        setUser(currentUser);
        if (!currentUser) {
          localStorage.clear();
          setClaims(null);
          setUserProfile(null);
          setActiveRoleState(null);
          Sentry.setUser(null);
          setLoading(false);
        } else {
          // Force refresh token to get latest claims from server
          // This ensures we have the most up-to-date custom claims
          const tokenResult = await currentUser.getIdTokenResult(true);
          const loadedClaims = tokenResult.claims as unknown as CustomClaims;
          setClaims(loadedClaims);

          // Record the web login + browser for support diagnosis (staff-only,
          // enforced server-side). Fire-and-forget.
          recordWebTelemetry("login", "signed_in", {
            role: (loadedClaims as any)?.role ?? null,
            surface: "web",
          });

          Sentry.setUser({
            id: currentUser.uid,
            email: currentUser.email || undefined,
            username: currentUser.displayName || undefined,
          });

          // Load Produktly only for admin users
          if (loadedClaims.role === 'admin') {
            loadProduktly(currentUser.uid);
          }

          setLoading(false);
        }
        setError(null);
      },
      (error) => {
        console.error('Auth state change error:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Subscribe to user profile for roles and preferences
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    const userDocRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setUserProfile({ id: docSnapshot.id, ...docSnapshot.data() } as UserProfile);
        } else {
          setUserProfile(null);
        }
      },
      (err) => {
        console.error("Error listening to user profile:", err);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Primary role from claims (highest priority role across all accounts)
  const role = claims?.role || null;

  // Get the current accountId
  const accountId = claims?.accountId || claims?.accountIds?.[0] || null;

  // Available roles for the current account from claims.accounts
  // Falls back to userProfile.roles for backwards compatibility, then to claims.role
  const availableRoles: UserRole[] = (() => {
    // First try to get roles from accounts claim for the current account
    if (accountId && claims?.accounts?.[accountId]?.roles?.length) {
      return claims.accounts[accountId].roles;
    }
    // Fall back to user profile roles for backwards compatibility
    if (userProfile?.roles && userProfile.roles.length > 0) {
      return userProfile.roles;
    }
    // Last resort: use claims.role if available
    return role ? [role] : [];
  })();

  // Resolve active role when claims, profile, or account changes
  useEffect(() => {
    const resolved = resolveActiveRole(
      accountId,
      availableRoles,
      claims?.role || null,
      userProfile?.preferences
    );
    setActiveRoleState(resolved);
  }, [claims, userProfile, availableRoles, accountId]);

  // Whether the user can switch roles (has more than one available role)
  const canSwitchRoles = availableRoles.length > 1;

  // Function to switch active role
  const setActiveRole = useCallback(async (newRole: UserRole) => {
    if (!availableRoles.includes(newRole)) {
      console.error("Cannot switch to role that is not available:", newRole);
      return;
    }

    if (!accountId) {
      console.error("Cannot switch role without an account");
      return;
    }

    // Update local state immediately for responsive UX
    setActiveRoleState(newRole);

    // Persist to localStorage
    setStoredActiveRole(accountId, newRole);

    // Persist to server (fire and forget, don't block UI)
    if (user) {
      const currentPrefs = userProfile?.preferences || {
        notifications: {
          email: true,
          sms: true,
          push: true,
          visitReminders: true,
          reportNotifications: true,
          paymentNotifications: true,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: 'en',
      };

      UserService.updateUser(user.uid, {
        preferences: {
          ...currentPrefs,
          activeRoleByAccount: {
            ...(currentPrefs.activeRoleByAccount || {}),
            [accountId]: newRole,
          },
        },
      }).catch((err) => {
        console.error("Failed to persist active role to server:", err);
      });
    }
  }, [availableRoles, accountId, user, userProfile]);

  // Computed properties for role checking - now based on activeRole instead of claims.role
  // Owner role has the same permissions as admin
  const isAdmin = activeRole === 'owner' || activeRole === 'admin';
  const isMonitor = activeRole === 'supervisor';
  const isParent = activeRole === 'parent';

  return (
    <AuthContext.Provider value={{
      user,
      claims,
      role,
      loading,
      error,
      isMonitor,
      isAdmin,
      isParent,
      signOut: handleSignOut,
      clearError,
      refreshClaims,
      // Role switching
      availableRoles,
      activeRole,
      setActiveRole,
      canSwitchRoles,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
